import React, { useState, useMemo } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonButtons,
  IonBackButton,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonIcon,
  IonLoading,
  IonRefresher,
  IonRefresherContent,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonChip,
  IonText,
  IonList,
  IonListHeader,
  IonAlert,
  IonModal,
  IonInput,
} from '@ionic/react';
import {
  refreshOutline,
  funnelOutline,
  downloadOutline,
  closeOutline,
  timeOutline,
  personOutline,
  documentTextOutline,
  alertCircleOutline,
  checkmarkCircleOutline,
  warningOutline,
} from 'ionicons/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  actionLogsApi,
  ACTION_LABELS,
  CATEGORY_LABELS,
  LEVEL_LABELS,
  LEVEL_COLORS
} from '../api/actionLogs';
import {
  ActionLogAction,
  ActionLogCategory,
  ActionLogLevel,
} from 'shared';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const ActionLogs: React.FC = () => {
  const { t } = useLanguage();
  const { colors } = useTheme();
  const queryClient = useQueryClient();

  // State for filtering and pagination
  const [filters, setFilters] = useState({
    category: '',
    action: '',
    level: '',
    search: '',
    startDate: '',
    endDate: '',
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportAlert, setShowExportAlert] = useState(false);

  const pageSize = 20;

  // Build query parameters
  const queryParams = useMemo(() => {
    const params: any = {
      page: currentPage,
      limit: pageSize,
    };

    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== '') {
        params[key] = value;
      }
    });

    return params;
  }, [filters, currentPage]);

  // Fetch action logs
  const {
    data: logsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['action-logs', queryParams],
    queryFn: () => actionLogsApi.getLogs(queryParams),
    staleTime: 30000,
  });

  // Fetch statistics
  const { data: stats } = useQuery({
    queryKey: ['action-logs-statistics'],
    queryFn: () => actionLogsApi.getStatistics(),
    refetchInterval: 60000, // Refresh every minute
  });

  const logs = logsData?.logs || [];
  const totalLogs = logsData?.total || 0;
  const totalPages = logsData?.totalPages || 1;

  // Handle refresh
  const handleRefresh = async (event: CustomEvent) => {
    await queryClient.invalidateQueries({ queryKey: ['action-logs'] });
    await queryClient.invalidateQueries({ queryKey: ['action-logs-statistics'] });
    event.detail.complete();
  };

  // Handle infinite scroll
  const handleInfiniteScroll = async (event: CustomEvent) => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
    event.detail.complete();
  };

  // Handle filter changes
  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      category: '',
      action: '',
      level: '',
      search: '',
      startDate: '',
      endDate: '',
    });
    setCurrentPage(1);
  };

  // Export logs
  const exportLogs = async () => {
    try {
      // Convert string filters to proper types for API
      const exportFilters: any = {};

      if (filters.category) exportFilters.category = filters.category as ActionLogCategory;
      if (filters.action) exportFilters.action = filters.action as ActionLogAction;
      if (filters.level) exportFilters.level = filters.level as ActionLogLevel;
      if (filters.search) exportFilters.search = filters.search;
      if (filters.startDate) exportFilters.startDate = filters.startDate;
      if (filters.endDate) exportFilters.endDate = filters.endDate;

      const blob = await actionLogsApi.exportLogs(exportFilters);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `action-logs-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      setShowExportAlert(false);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Format date
  const formatDate = (date: string | Date) => {
    return new Date(date).toLocaleString();
  };

  // Get level icon and color
  const getLevelDisplay = (level: ActionLogLevel) => ({
    icon: level === ActionLogLevel.CRITICAL || level === ActionLogLevel.HIGH
      ? alertCircleOutline
      : level === ActionLogLevel.MEDIUM
        ? warningOutline
        : checkmarkCircleOutline,
    color: LEVEL_COLORS[level],
    label: LEVEL_LABELS[level],
  });

  // Get category icon
  const getCategoryIcon = (category: ActionLogCategory) => {
    switch (category) {
      case ActionLogCategory.AUTH:
        return personOutline;
      case ActionLogCategory.ORDER:
        return documentTextOutline;
      case ActionLogCategory.FINANCIAL:
        return alertCircleOutline;
      default:
        return documentTextOutline;
    }
  };

  if (error) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/tabs/more" />
            </IonButtons>
            <IonTitle>{t('actionLogs') || 'Action Logs'}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p>Error loading action logs</p>
            <IonButton fill="clear" onClick={() => refetch()}>
              {t('retry') || 'Retry'}
            </IonButton>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/more" />
          </IonButtons>
          <IonTitle>{t('actionLogs') || 'Action Logs'}</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={() => setShowFilters(true)}>
              <IonIcon icon={funnelOutline} />
            </IonButton>
            <IonButton fill="clear" onClick={() => setShowExportAlert(true)}>
              <IonIcon icon={downloadOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent
            pullingIcon={refreshOutline}
            pullingText={t('pullToRefresh') || 'Pull to refresh'}
          />
        </IonRefresher>

        {/* Statistics Cards */}
        {stats && (
          <div style={{ padding: '16px' }}>
            <IonCard>
              <IonCardHeader>
                <IonCardTitle style={{ fontSize: '18px' }}>
                  {t('overview') || 'Overview'}
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div style={{ textAlign: 'center' }}>
                    <h3 style={{ margin: 0, color: colors.text, fontSize: '24px' }}>
                      {stats.totalLogs}
                    </h3>
                    <p style={{ margin: 0, color: colors.textSecondary, fontSize: '12px' }}>
                      {t('totalLogs') || 'Total Logs'}
                    </p>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <h3 style={{ margin: 0, color: colors.text, fontSize: '24px' }}>
                      {stats.logsByCategory[ActionLogCategory.AUTH] || 0}
                    </h3>
                    <p style={{ margin: 0, color: colors.textSecondary, fontSize: '12px' }}>
                      {t('authEvents') || 'Auth Events'}
                    </p>
                  </div>
                  <div style={{ textAlign: 'center' }}>
                    <h3 style={{ margin: 0, color: colors.text, fontSize: '24px' }}>
                      {stats.logsByLevel[ActionLogLevel.HIGH] + stats.logsByLevel[ActionLogLevel.CRITICAL] || 0}
                    </h3>
                    <p style={{ margin: 0, color: colors.textSecondary, fontSize: '12px' }}>
                      {t('criticalEvents') || 'Critical Events'}
                    </p>
                  </div>
                </div>

                {/* Quick category chips */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {Object.entries(stats.logsByCategory).map(([category, count]) => (
                    <IonChip
                      key={category}
                      color="primary"
                      onClick={() => handleFilterChange('category', category)}
                      style={{ cursor: 'pointer' }}
                    >
                      <IonLabel>{CATEGORY_LABELS[category as ActionLogCategory]} ({count})</IonLabel>
                    </IonChip>
                  ))}
                </div>
              </IonCardContent>
            </IonCard>
          </div>
        )}

        {/* Active Filters */}
        {(filters.category || filters.action || filters.level || filters.search) && (
          <div style={{ padding: '0 16px 16px' }}>
            <IonCard>
              <IonCardContent>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <IonText color="medium">
                    <h4 style={{ margin: 0 }}>{t('activeFilters') || 'Active Filters'}</h4>
                  </IonText>
                  <IonButton fill="clear" size="small" onClick={clearFilters}>
                    {t('clearFilters') || 'Clear Filters'}
                  </IonButton>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' }}>
                  {filters.category && (
                    <IonChip color="primary">
                      <IonLabel>{CATEGORY_LABELS[filters.category as ActionLogCategory]}</IonLabel>
                    </IonChip>
                  )}
                  {filters.action && (
                    <IonChip color="secondary">
                      <IonLabel>{ACTION_LABELS[filters.action as ActionLogAction]}</IonLabel>
                    </IonChip>
                  )}
                  {filters.level && (
                    <IonChip color="warning">
                      <IonLabel>{LEVEL_LABELS[filters.level as ActionLogLevel]}</IonLabel>
                    </IonChip>
                  )}
                  {filters.search && (
                    <IonChip color="tertiary">
                      <IonLabel>Search: &quot;{filters.search}&quot;</IonLabel>
                    </IonChip>
                  )}
                </div>
              </IonCardContent>
            </IonCard>
          </div>
        )}

        {/* Logs List */}
        <IonList>
          <IonListHeader>
            <IonLabel>
              {t('recentActivity') || 'Recent Activity'} ({totalLogs} records)
            </IonLabel>
          </IonListHeader>

          {logs.map((log) => {
            const levelDisplay = getLevelDisplay(log.level);
            return (
              <IonItem key={log.id} style={{ '--min-height': '80px' }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  width: '100%',
                  padding: '8px 0'
                }}>
                  {/* Level indicator */}
                  <div style={{ marginTop: '2px' }}>
                    <IonIcon
                      icon={levelDisplay.icon}
                      style={{
                        color: levelDisplay.color,
                        fontSize: '18px'
                      }}
                    />
                  </div>

                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                      <IonIcon
                        icon={getCategoryIcon(log.category)}
                        style={{
                          color: colors.textSecondary,
                          fontSize: '16px'
                        }}
                      />
                      <IonText style={{
                        fontWeight: '600',
                        color: colors.text,
                        fontSize: '14px'
                      }}>
                        {ACTION_LABELS[log.action]}
                      </IonText>
                    </div>

                    <IonText style={{
                      color: colors.text,
                      fontSize: '14px',
                      lineHeight: '1.4'
                    }}>
                      {log.description}
                    </IonText>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                      <IonText style={{
                        color: colors.textSecondary,
                        fontSize: '12px'
                      }}>
                        <IonIcon icon={timeOutline} style={{ marginRight: '4px' }} />
                        {formatDate(log.createdAt)}
                      </IonText>
                      {log.user?.username && (
                        <IonText style={{
                          color: colors.textSecondary,
                          fontSize: '12px'
                        }}>
                          <IonIcon icon={personOutline} style={{ marginRight: '4px' }} />
                          {log.user.username}
                        </IonText>
                      )}
                    </div>

                    {/* Resource info */}
                    {log.resourceName && (
                      <div style={{ marginTop: '4px' }}>
                        <IonChip color="light" style={{ height: '24px', fontSize: '11px' }}>
                          <IonLabel>{log.resourceName}</IonLabel>
                        </IonChip>
                      </div>
                    )}
                  </div>
                </div>
              </IonItem>
            );
          })}
        </IonList>

        {logs.length === 0 && !isLoading && (
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <IonIcon
              icon={documentTextOutline}
              style={{ fontSize: '64px', color: colors.textTertiary, marginBottom: '16px' }}
            />
            <h3 style={{ color: colors.text }}>
              {filters.category || filters.action || filters.level || filters.search
                ? (t('noLogsMatchFilters') || 'No logs match your filters')
                : (t('noActionLogs') || 'No action logs found')
              }
            </h3>
            <p style={{ color: colors.textSecondary }}>
              {filters.category || filters.action || filters.level || filters.search
                ? (t('tryAdjustingFilters') || 'Try adjusting your filters')
                : (t('actionLogsWillAppear') || 'Action logs will appear here as users interact with the system')
              }
            </p>
          </div>
        )}

        <IonInfiniteScroll
          onIonInfinite={handleInfiniteScroll}
          threshold="100px"
          disabled={currentPage >= totalPages}
        >
          <IonInfiniteScrollContent loadingText={t('loadingMore') || 'Loading more...'} />
        </IonInfiniteScroll>

        <IonLoading isOpen={isLoading} message={t('loading') || 'Loading...'} />

        {/* Filters Modal */}
        <IonModal isOpen={showFilters} onDidDismiss={() => setShowFilters(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('filters') || 'Filters'}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowFilters(false)}>
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent style={{ padding: '20px' }}>
            <IonItem>
              <IonLabel position="stacked">{t('search') || 'Search'}</IonLabel>
              <IonInput
                value={filters.search}
                onIonInput={(e) => handleFilterChange('search', e.detail.value || '')}
                placeholder={t('searchLogs') || 'Search logs...'}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">{t('category') || 'Category'}</IonLabel>
              <IonSelect
                value={filters.category}
                onIonChange={(e) => handleFilterChange('category', e.detail.value)}
                placeholder={t('allCategories') || 'All Categories'}
              >
                <IonSelectOption value="">{t('all') || 'All'}</IonSelectOption>
                {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                  <IonSelectOption key={key} value={key}>
                    {label}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Action</IonLabel>
              <IonSelect
                value={filters.action}
                onIonChange={(e) => handleFilterChange('action', e.detail.value)}
                placeholder="All Actions"
              >
                <IonSelectOption value="">{t('all') || 'All'}</IonSelectOption>
                {Object.entries(ACTION_LABELS).map(([key, label]) => (
                  <IonSelectOption key={key} value={key}>
                    {label}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Level</IonLabel>
              <IonSelect
                value={filters.level}
                onIonChange={(e) => handleFilterChange('level', e.detail.value)}
                placeholder="All Levels"
              >
                <IonSelectOption value="">{t('all') || 'All'}</IonSelectOption>
                {Object.entries(LEVEL_LABELS).map(([key, label]) => (
                  <IonSelectOption key={key} value={key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '12px',
                          height: '12px',
                          borderRadius: '50%',
                          backgroundColor: LEVEL_COLORS[key as ActionLogLevel],
                        }}
                      />
                      {label}
                    </div>
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <div style={{ marginTop: '24px', display: 'flex', gap: '12px' }}>
              <IonButton
                expand="block"
                fill="outline"
                onClick={clearFilters}
              >
                {t('clearFilters') || 'Clear Filters'}
              </IonButton>
              <IonButton
                expand="block"
                onClick={() => setShowFilters(false)}
              >
                {t('applyFilters') || 'Apply Filters'}
              </IonButton>
            </div>
          </IonContent>
        </IonModal>

        {/* Export Alert */}
        <IonAlert
          isOpen={showExportAlert}
          onDidDismiss={() => setShowExportAlert(false)}
          header={t('exportLogs') || 'Export Logs'}
          message={t('exportLogsMessage') || 'Export current filtered logs to CSV file. This will download all logs matching your current filters.'}
          buttons={[
            {
              text: t('cancel') || 'Cancel',
              role: 'cancel',
            },
            {
              text: t('export') || 'Export',
              handler: exportLogs,
            },
          ]}
        />
      </IonContent>
    </IonPage>
  );
};

export default ActionLogs;